'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

type Role = 'employer' | 'employee';
type AuthMode = 'login' | 'signup';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName?: string;
  industry?: string;
  currentRole?: string;
  experienceLevel?: string;
  agreeTerms: boolean;
}

const mockCredentials = [
  { role: 'employer' as Role, label: 'Employer', email: 'recruiter@techcorp.io', password: 'Vault2026!' },
  { role: 'employee' as Role, label: 'Job Seeker', email: 'aiden.kowalski@promail.com', password: 'Seeker2026!' },
];

export default function AuthScreen() {
  const [role, setRole] = useState<Role>('employer');
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loginForm = useForm<LoginFormData>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupFormData>({ defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', agreeTerms: false } });

  const handleLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    // TODO: Connect to authentication API — POST /api/auth/login
    await new Promise((r) => setTimeout(r, 1200));
    const cred = mockCredentials.find((c) => c.role === role);
    if (data.email === cred?.email && data.password === cred?.password) {
      toast.success(`Welcome back! Redirecting to your ${role === 'employer' ? 'dashboard' : 'profile'}...`);
      setTimeout(() => {
        if (role === 'employer') {
          window.location.href = '/resume-search-discovery';
        } else {
          window.location.href = '/employee-profile-resume-management';
        }
      }, 1000);
    } else {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
      loginForm.setError('email', { message: ' ' });
      loginForm.setError('password', { message: 'Invalid credentials — use the demo accounts below to sign in' });
    }
    setIsLoading(false);
  };

  const handleSignupSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    // TODO: Connect to user registration API — POST /api/auth/register
    await new Promise((r) => setTimeout(r, 1400));
    toast.success('Account created! Check your email to verify.');
    setIsLoading(false);
  };

  const autofillCredentials = (cred: typeof mockCredentials[0]) => {
    setRole(cred.role);
    setMode('login');
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    toast.info(`Demo credentials filled for ${cred.label}`);
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[52%] relative flex-col justify-between p-10 bg-gradient-brand overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute top-1/2 -right-20 w-72 h-72 rounded-full bg-accent/8 blur-3xl" />
          <div className="absolute -bottom-20 left-1/3 w-64 h-64 rounded-full bg-primary/8 blur-3xl" />
          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <AppLogo size={40} />
          <span className="text-xl font-800 text-foreground tracking-tight">Hiretecbox.com</span>
        </div>

        {/* Hero content */}
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 text-violet-300 text-xs font-600 px-3 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-slow" />
            2,400+ Active Candidates This Week
          </div>
          <h1 className="text-4xl font-800 text-foreground leading-tight mb-4">
            The resume database<br />
            <span className="text-gradient-violet">built for serious hiring.</span>
          </h1>
          <p className="text-base text-secondary-foreground leading-relaxed max-w-sm">
            Search pre-screened candidates by skill, experience, and availability. No noise — just talent.
          </p>

          {/* Role benefit cards */}
          <div className="mt-8 space-y-3">
            {[
              {
                icon: 'MagnifyingGlassIcon',
                title: 'For Employers',
                desc: 'Search 40,000+ verified profiles with advanced skill filters',
                color: 'text-violet-400',
                bg: 'bg-primary/10 border-primary/20',
              },
              {
                icon: 'UserCircleIcon',
                title: 'For Job Seekers',
                desc: 'Get discovered by 1,200+ hiring companies actively recruiting',
                color: 'text-amber-400',
                bg: 'bg-accent/10 border-accent/20',
              },
            ].map((item) => (
              <div
                key={`benefit-${item.title}`}
                className={`flex items-start gap-3 p-4 rounded-xl border ${item.bg} backdrop-blur-sm`}
              >
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={20} className={item.color} />
                <div>
                  <p className="text-sm font-700 text-foreground">{item.title}</p>
                  <p className="text-xs text-secondary-foreground mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom stats */}
        <div className="relative flex items-center gap-6">
          {[
            { value: '40K+', label: 'Verified Profiles' },
            { value: '1.2K', label: 'Active Employers' },
            { value: '94%', label: 'Hire Rate' },
          ].map((stat) => (
            <div key={`stat-${stat.label}`}>
              <p className="text-2xl font-800 text-foreground font-tabular">{stat.value}</p>
              <p className="text-xs text-secondary-foreground">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="text-lg font-800 text-foreground">Hiretecbox.com</span>
          </div>

          {/* Mode toggle */}
          <div className="flex items-center gap-1 bg-muted rounded-xl p-1 mb-6">
            {(['login', 'signup'] as const).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2 text-sm font-600 rounded-lg transition-all duration-200 ${
                  mode === m
                    ? 'bg-card text-foreground shadow-card'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Role selector */}
          <div className="mb-6">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-2">I am a</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'employer' as Role, label: 'Employer', icon: 'BuildingOfficeIcon', desc: 'Hiring talent' },
                { value: 'employee' as Role, label: 'Job Seeker', icon: 'UserIcon', desc: 'Finding work' },
              ].map((r) => (
                <button
                  key={`role-${r.value}`}
                  type="button"
                  onClick={() => setRole(r.value)}
                  className={`relative flex flex-col items-start gap-1 p-4 rounded-xl border transition-all duration-200 text-left ${
                    role === r.value
                      ? 'bg-primary/10 border-primary text-foreground shadow-card'
                      : 'bg-card border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {role === r.value && (
                    <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                      <Icon name="CheckIcon" size={10} className="text-white" />
                    </span>
                  )}
                  <Icon
                    name={r.icon as Parameters<typeof Icon>[0]['name']}
                    size={20}
                    className={role === r.value ? 'text-primary' : 'text-muted-foreground'}
                  />
                  <span className="text-sm font-700">{r.label}</span>
                  <span className="text-xs">{r.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Social auth */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            {[
              { label: 'Google', icon: 'GlobeAltIcon' },
              { label: 'LinkedIn', icon: 'LinkIcon' },
            ].map((provider) => (
              <button
                key={`social-${provider.label}`}
                type="button"
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-sm font-500 text-foreground hover:border-primary/50 hover:bg-muted transition-all duration-150 active:scale-95"
              >
                <Icon name={provider.icon as Parameters<typeof Icon>[0]['name']} size={16} />
                {provider.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or with email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)} className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-600 text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  placeholder="you@company.com"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                  className={`w-full bg-input border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                    loginForm.formState.errors.email ? 'border-rose-500/70' : 'border-border focus:border-primary'
                  }`}
                />
                {loginForm.formState.errors.email?.message && loginForm.formState.errors.email.message.trim() && (
                  <p className="mt-1 text-xs text-rose-400">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="login-password" className="block text-sm font-600 text-foreground mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    {...loginForm.register('password', { required: 'Password is required', minLength: { value: 6, message: 'Minimum 6 characters' } })}
                    className={`w-full bg-input border rounded-xl px-4 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                      loginForm.formState.errors.password ? 'border-rose-500/70' : 'border-border focus:border-primary'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1 text-xs text-rose-400">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...loginForm.register('remember')}
                    className="w-4 h-4 rounded border-border bg-input accent-primary"
                  />
                  <span className="text-sm text-muted-foreground">Remember me</span>
                </label>
                <button type="button" className="text-sm text-primary hover:text-violet-300 transition-colors">
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-violet-500 text-white font-700 py-3 rounded-xl transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ minHeight: '48px' }}
              >
                {isLoading ? (
                  <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                ) : (
                  <>
                    Sign In as {role === 'employer' ? 'Employer' : 'Job Seeker'}
                    <Icon name="ArrowRightIcon" size={16} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Signup Form */}
          {mode === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)} className="space-y-4">
              <div>
                <label htmlFor="signup-name" className="block text-sm font-600 text-foreground mb-1.5">
                  {role === 'employer' ? 'Your Full Name' : 'Full Name'}
                </label>
                <input
                  id="signup-name"
                  type="text"
                  placeholder="e.g. Sarah Mitchell"
                  {...signupForm.register('fullName', { required: 'Full name is required' })}
                  className={`w-full bg-input border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                    signupForm.formState.errors.fullName ? 'border-rose-500/70' : 'border-border focus:border-primary'
                  }`}
                />
                {signupForm.formState.errors.fullName && (
                  <p className="mt-1 text-xs text-rose-400">{signupForm.formState.errors.fullName.message}</p>
                )}
              </div>

              {role === 'employer' && (
                <>
                  <div>
                    <label htmlFor="signup-company" className="block text-sm font-600 text-foreground mb-1.5">
                      Company Name
                    </label>
                    <input
                      id="signup-company"
                      type="text"
                      placeholder="e.g. TechCorp Inc."
                      {...signupForm.register('companyName', { required: role === 'employer' ? 'Company name is required' : false })}
                      className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="signup-industry" className="block text-sm font-600 text-foreground mb-1.5">
                      Industry
                    </label>
                    <select
                      id="signup-industry"
                      {...signupForm.register('industry')}
                      className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all"
                    >
                      <option value="">Select industry</option>
                      {['Technology', 'Finance', 'Healthcare', 'Marketing', 'Design', 'Operations', 'Sales', 'Legal'].map((ind) => (
                        <option key={`ind-${ind}`} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {role === 'employee' && (
                <>
                  <div>
                    <label htmlFor="signup-current-role" className="block text-sm font-600 text-foreground mb-1.5">
                      Current / Most Recent Role
                    </label>
                    <input
                      id="signup-current-role"
                      type="text"
                      placeholder="e.g. Senior Frontend Engineer"
                      {...signupForm.register('currentRole')}
                      className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="signup-exp" className="block text-sm font-600 text-foreground mb-1.5">
                      Experience Level
                    </label>
                    <select
                      id="signup-exp"
                      {...signupForm.register('experienceLevel')}
                      className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all"
                    >
                      <option value="">Select level</option>
                      {['Entry Level (0–2 yrs)', 'Mid Level (3–5 yrs)', 'Senior (6–9 yrs)', 'Lead / Principal (10+ yrs)', 'Executive / Director'].map((lvl) => (
                        <option key={`exp-${lvl}`} value={lvl}>{lvl}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div>
                <label htmlFor="signup-email" className="block text-sm font-600 text-foreground mb-1.5">
                  Work Email
                </label>
                <input
                  id="signup-email"
                  type="email"
                  placeholder={role === 'employer' ? 'you@company.com' : 'you@email.com'}
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                  className={`w-full bg-input border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                    signupForm.formState.errors.email ? 'border-rose-500/70' : 'border-border focus:border-primary'
                  }`}
                />
                {signupForm.formState.errors.email && (
                  <p className="mt-1 text-xs text-rose-400">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-sm font-600 text-foreground mb-1.5">
                  Password
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters, at least one number</p>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                      pattern: { value: /(?=.*[0-9])/, message: 'Must contain at least one number' },
                    })}
                    className={`w-full bg-input border rounded-xl px-4 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                      signupForm.formState.errors.password ? 'border-rose-500/70' : 'border-border focus:border-primary'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="mt-1 text-xs text-rose-400">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-confirm-password" className="block text-sm font-600 text-foreground mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="signup-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Repeat your password"
                    {...signupForm.register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (val) => val === signupForm.watch('password') || 'Passwords do not match',
                    })}
                    className={`w-full bg-input border rounded-xl px-4 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                      signupForm.formState.errors.confirmPassword ? 'border-rose-500/70' : 'border-border focus:border-primary'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon name={showConfirmPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-xs text-rose-400">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...signupForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                  className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary"
                />
                <span className="text-sm text-muted-foreground">
                  I agree to the{' '}
                  <span className="text-primary hover:text-violet-300 cursor-pointer transition-colors">Terms of Service</span>
                  {' '}and{' '}
                  <span className="text-primary hover:text-violet-300 cursor-pointer transition-colors">Privacy Policy</span>
                </span>
              </label>
              {signupForm.formState.errors.agreeTerms && (
                <p className="text-xs text-rose-400">{signupForm.formState.errors.agreeTerms.message}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-violet-500 text-white font-700 py-3 rounded-xl transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ minHeight: '48px' }}
              >
                {isLoading ? (
                  <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                ) : (
                  <>
                    Create {role === 'employer' ? 'Employer' : 'Job Seeker'} Account
                    <Icon name="ArrowRightIcon" size={16} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Demo credentials */}
          <div className="mt-6 bg-muted/60 border border-border rounded-xl p-4">
            <p className="text-xs font-700 text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Icon name="KeyIcon" size={12} />
              Demo Accounts — click to autofill
            </p>
            <div className="space-y-2">
              {mockCredentials.map((cred) => (
                <div
                  key={`cred-${cred.role}`}
                  className="flex items-center justify-between gap-3 bg-card border border-border rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`text-xs font-600 px-2 py-0.5 rounded-full flex-shrink-0 ${
                        cred.role === 'employer' ?'bg-primary/20 text-violet-300' :'bg-accent/20 text-amber-300'
                      }`}
                    >
                      {cred.label}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">{cred.email}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => autofillCredentials(cred)}
                    className="flex items-center gap-1 text-xs font-600 text-primary hover:text-violet-300 transition-colors flex-shrink-0"
                  >
                    <Icon name="ArrowTopRightOnSquareIcon" size={12} />
                    Use
                  </button>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-4">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
              className="text-primary hover:text-violet-300 font-600 transition-colors"
            >
              {mode === 'login' ? 'Sign up free' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}